import { useRef, useMemo, useState, forwardRef } from 'react';
import * as React from 'react';
import classnames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import styles from './styles.module.scss';

import { useTheme } from 'components/theme';

const TEXT_FIELD_SIZE_CLASSES = {
  large: styles.sizeLarge,
  medium: styles.sizeMedium,
  small: styles.sizeSmall,
};

const TEXT_FIELD_THEME_CLASSES = {
  dark: styles.dark,
  light: styles.light,
};

type NativeInputProps = React.HTMLProps<HTMLInputElement>;

type TextFieldPropsBase = {
  size?: 'large' | 'medium' | 'small';
  disabled?: boolean;
  width?: React.ReactText;
  error?: boolean;
  helperText?: React.ReactNode;

  leadingIcon?: React.ReactNode;
  leadingPrefix?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  trailingSuffix?: React.ReactNode;

  'data-cy'?: string;
};

type TextFieldPropsLeading =
  | { leadingIcon?: React.ReactNode }
  | { leadingPrefix?: React.ReactNode };

type TextFieldPropsTrailing =
  | { trailingIcon?: React.ReactNode }
  | { trailingSuffix?: React.ReactNode };

type TextFieldPropsCombined = TextFieldPropsBase &
  TextFieldPropsLeading &
  TextFieldPropsTrailing;

// Allow passing native input props, but Omit those that we define in ButtonPropsBase so they don't conflict
export type TextFieldProps = Omit<
  NativeInputProps,
  keyof TextFieldPropsCombined
> &
  TextFieldPropsCombined;

const TextFieldRaw: React.FunctionComponent<
  TextFieldProps & { forwardedRef: any }
> = (props) => {
  const {
    forwardedRef,

    error = false,
    disabled = false,
    leadingIcon,
    leadingPrefix,
    trailingIcon,
    trailingSuffix,
    width,
    size = 'large',
    helperText = null,

    type = 'text',
    placeholder,
    readOnly,
    onFocus,
    onBlur,
    ...restProps
  } = props;
  const dataCy = props['data-cy'];

  const [focus, setFocus] = useState(false);
  const defaultInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = forwardedRef || defaultInputRef;

  // Give an id to the raw input element so it can be targeted for accessibility reasons
  const inputId = useMemo(() => uuidv4(), []);

  const theme = useTheme();

  return (
    <div
      className={classnames(
        styles.textfield,
        TEXT_FIELD_SIZE_CLASSES[size],
        TEXT_FIELD_THEME_CLASSES[theme],
        {
          [styles.focused]: focus,
          [styles.error]: error,
          [styles.disabled]: disabled,
        }
      )}
      style={{ width }}
      // Clicking on anything in the wrapping div that isn't the input should focus the input
      onMouseDown={(e) => {
        if (e.target === inputRef.current) {
          return;
        }

        e.preventDefault();
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }}
    >
      <div className={styles.left}>
        {leadingIcon ? (
          <div className={classnames(styles.leading, styles.icon)}>
            {leadingIcon}
          </div>
        ) : null}
        {leadingPrefix ? (
          <div className={classnames(styles.leading, styles.prefix)}>
            {leadingPrefix}
          </div>
        ) : null}
        <input
          {...restProps}
          role="none"
          ref={inputRef}
          type={type}
          id={inputId}
          disabled={disabled}
          placeholder={placeholder}
          readOnly={readOnly}
          onFocus={(...args) => {
            setFocus(true);
            if (onFocus) {
              onFocus(...args);
            }
          }}
          onBlur={(...args) => {
            setFocus(false);
            if (onBlur) {
              onBlur(...args);
            }
          }}
          data-cy={dataCy}
        />
      </div>
      <div className={styles.right}>
        {trailingIcon ? (
          <div className={classnames(styles.trailing, styles.icon)}>
            {trailingIcon}
          </div>
        ) : null}
        {trailingSuffix ? (
          <div className={classnames(styles.trailing, styles.suffix)}>
            {trailingSuffix}
          </div>
        ) : null}
      </div>
      {helperText ? (
        <div className={styles.helperText}>{helperText}</div>
      ) : null}
    </div>
  );
};

const TextField = forwardRef((props: TextFieldProps, ref) => (
  <TextFieldRaw {...props} forwardedRef={ref} />
));

export default TextField;
